import { useEffect, useLayoutEffect, useState } from "react";
import { Routes, Route, useLocation, Navigate, Outlet, useSearchParams } from "react-router-dom";
import { InsightsPageProvider } from "./context/InsightsPageContext";
import { CareersPageProvider } from "./context/CareersPageContext";

import Home from "./pages/Home/Home";
import Preloader from "./components/shared/Preloader/Preloader";
import Services from "./pages/Services/Services";
import About from "./pages/About/About";
import Mobility from "./pages/Industries/Mobility";
import Healthcare from "./pages/Industries/Healthcare";
import RetailEcommerce from "./pages/Industries/RetailEcommerce";
import FinTech from "./pages/Industries/FinTech";
import TravelHospitality from "./pages/Industries/TravelHospitality";
import HighTechSoftware from "./pages/Industries/HighTechSoftware";
import TelecomMedia from "./pages/Industries/TelecomMedia";
import RealEstateConstruction from "./pages/Industries/RealEstateConstruction";
import AiDataIntelligence from "./pages/Industries/AiDataIntelligence";
import Insights from "./pages/Insights/Insights";
import ArticleDetail from "./components/pages/Insights/ArticleDetail/ArticleDetail";
import { GlobalLayoutProvider } from "./context/GlobalLayoutContext";
import Work from "./pages/Work/Work";
import ProjectDetails from "./components/pages/Work/ProjectDetails/ProjectDetails";
import Careers from "./pages/Careers/Careers";
import OpenPositionDetails from "./components/pages/Careers/OpenPositionDetails/OpenPositionDetails";
import ContactUs from "./pages/ContactUs/ContactUs";
import Quote from "./pages/Quote/Quote";
import Development from "./pages/Services/Development";
import Design from "./pages/Services/Design";
import Engineering from "./pages/Services/Engineering";
import Transformation from "./pages/Services/Transformation";
import Ai from "./pages/Services/Ai";
import Infrastructure from "./pages/Services/Infrastructure";
import OrderHosting from "./pages/Services/OrderHosting";
import { startScrollReveal } from "./utils/scrollReveal";
import { SERVICE_PAGE_SLUGS } from "./components/shared/Navbar/ServicesDropDown";
import { INDUSTRY_PAGE_SLUGS } from "./components/shared/Navbar/IndustryDropdown";

function LegacyIndustryRedirect() {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\//, "").split("/")[1];
  if (slug && INDUSTRY_PAGE_SLUGS.includes(slug)) {
    return <Navigate to={`/${slug}`} replace />;
  }
  return <Navigate to="/healthcare" replace />;
}

function LegacyServiceRedirect() {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const parts = pathname.replace(/^\//, "").split("/");
  const slug = parts[1];

  if (slug && SERVICE_PAGE_SLUGS.includes(slug)) {
    return <Navigate to={`/${slug}`} replace />;
  }
  if (searchParams.get("hosting") === "1") {
    return <Navigate to="/infrastructure" replace />;
  }
  return <Navigate to="/development" replace />;
}

export default function MarketingApp() {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [allMediaLoaded, setAllMediaLoaded] = useState(false);
  const [totalMedia, setTotalMedia] = useState(0);
  const [loadedMedia, setLoadedMedia] = useState(0);

  // Runs before the new route paints, so the scroll reset and the entrance
  // animations can never be seen fighting each other.
  useLayoutEffect(() => {
    document.documentElement.classList.remove("v-anim-ready");
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname]);

  useLayoutEffect(() => {
    const root = document.documentElement;
    root.classList.add("v-anim");
    return () => {
      root.classList.remove("v-anim");
      root.classList.remove("v-anim-ready");
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    let hasError = false;
    const cleanupFns = [];

    setIsLoading(true);
    setAllMediaLoaded(false);
    setLoadedMedia(0);

    const mediaElements = Array.from(document.querySelectorAll("img, video"));
    setTotalMedia(mediaElements.length);

    if (mediaElements.length === 0) {
      setAllMediaLoaded(true);
      return undefined;
    }

    let pending = mediaElements.length;
    let loaded = 0;

    const finishOne = () => {
      if (cancelled || hasError) return;
      loaded += 1;
      setLoadedMedia(loaded);
      pending -= 1;
      if (pending <= 0) {
        setAllMediaLoaded(true);
      }
    };

    const failOne = () => {
      hasError = true;
    };

    mediaElements.forEach((el) => {
      let settled = false;
      const resolveOne = () => {
        if (settled) return;
        settled = true;
        finishOne();
      };
      const rejectOne = () => {
        if (settled) return;
        settled = true;
        failOne();
      };

      if (el.tagName.toLowerCase() === "img") {
        const img = el;
        if (img.complete && img.naturalWidth > 0) {
          resolveOne();
          return;
        }
        const onLoad = () => resolveOne();
        const onError = () => rejectOne();
        img.addEventListener("load", onLoad, { once: true });
        img.addEventListener("error", onError, { once: true });
        cleanupFns.push(() => {
          img.removeEventListener("load", onLoad);
          img.removeEventListener("error", onError);
        });
        return;
      }

      const video = el;
      if (video.readyState >= 3) {
        resolveOne();
        return;
      }
      const onLoadedData = () => resolveOne();
      const onCanPlayThrough = () => resolveOne();
      const onError = () => rejectOne();
      video.addEventListener("loadeddata", onLoadedData, { once: true });
      video.addEventListener("canplaythrough", onCanPlayThrough, { once: true });
      video.addEventListener("error", onError, { once: true });
      cleanupFns.push(() => {
        video.removeEventListener("loadeddata", onLoadedData);
        video.removeEventListener("canplaythrough", onCanPlayThrough);
        video.removeEventListener("error", onError);
      });
    });

    return () => {
      cancelled = true;
      cleanupFns.forEach((fn) => fn());
    };
  }, [location.pathname]);

  // Entrance animations start once the preloader is gone and the page is
  // already scrolled to the top.
  useEffect(() => {
    if (isLoading) return undefined;

    document.documentElement.classList.add("v-anim-ready");
    return startScrollReveal();
  }, [isLoading, location.pathname]);

  return (
    <GlobalLayoutProvider>
      <div className="App">
        {isLoading && (
          <Preloader
            loadedMedia={loadedMedia}
            totalMedia={totalMedia}
            allMediaLoaded={allMediaLoaded}
            onFinish={() => setIsLoading(false)}
          />
        )}
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/services" element={<Services />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/industries" element={<Navigate to="/healthcare" replace />} />
          <Route path="/mobility" element={<Mobility />} />
          <Route path="/healthcare" element={<Healthcare />} />
          <Route path="/retail-ecommerce" element={<RetailEcommerce />} />
          <Route path="/fintech" element={<FinTech />} />
          <Route path="/travel-hospitality" element={<TravelHospitality />} />
          <Route path="/high-tech-software" element={<HighTechSoftware />} />
          <Route path="/telecom-media" element={<TelecomMedia />} />
          <Route path="/real-estate-construction" element={<RealEstateConstruction />} />
          <Route path="/ai-data-intelligence" element={<AiDataIntelligence />} />
          <Route path="/industry" element={<LegacyIndustryRedirect />} />
          <Route path="/industry/:industrySlug" element={<LegacyIndustryRedirect />} />
          <Route path="/orderhost" element={<OrderHosting />} />
          <Route path="/work" element={<Work />} />
          <Route path="/quote" element={<Quote />} />
          <Route path="/design" element={<Design />} />
          <Route path="/development" element={<Development />} />
          <Route path="/engineering" element={<Engineering />} />
          <Route path="/transformation" element={<Transformation />} />
          <Route path="/ai" element={<Ai />} />
          <Route path="/infrastructure" element={<Infrastructure />} />
          <Route path="/service" element={<LegacyServiceRedirect />} />
          <Route path="/service/:serviceSlug" element={<LegacyServiceRedirect />} />
          <Route
            element={
              <InsightsPageProvider>
                <Outlet />
              </InsightsPageProvider>
            }
          >
            <Route path="/insights" element={<Insights />} />
            <Route path="/insights/:slug" element={<ArticleDetail />} />
          </Route>
          <Route path="/projectdetail" element={<ProjectDetails />} />
          <Route path="/projectdetail/:slug" element={<ProjectDetails />} />
          <Route
            element={
              <CareersPageProvider>
                <Outlet />
              </CareersPageProvider>
            }
          >
            <Route path="/careers" element={<Careers />} />
            <Route path="/position/:slug" element={<OpenPositionDetails />} />
            <Route path="/position" element={<Navigate to="/careers" replace />} />
          </Route>
        </Routes>
      </div>
    </GlobalLayoutProvider>
  );
}
